import { type CSSProperties, useEffect, useState } from "react";

/**
 * What the app shows while the auth bootstrap is still running.
 *
 * `AuthGuard` and `AccountProvider` sit above every other provider and each waits on a GraphQL
 * request with no timeout (`RefreshToken`, then `MyAccount`). Both used to render `null`, so the
 * whole viewport was the bare `<body>` background and a stalled bootstrap was indistinguishable
 * from a dead app — the "gray screen of death" in B-1927, with no message and no way out.
 *
 * Styled with inline `var(--color-*)` reads rather than utility classes: `@qw/auth` does not depend
 * on `@qw/ui`, and `qw-ui.css` does not `@source` this package, so Tailwind classes written here
 * would never be generated. The tokens are custom properties on `:root`, so the active theme
 * (including `.dark`) still applies.
 */

/** Grace period before the bootstrap says anything, so a fast boot doesn't flash a message. */
const VISIBLE_AFTER_MS = 400;
/** How long the bootstrap may run before we call it stuck and offer a way out. */
const STUCK_AFTER_MS = 15_000;

type BootstrapPhase = "quiet" | "waiting" | "stuck";

const phaseForElapsed = (elapsedMs: number): BootstrapPhase => {
  if (elapsedMs >= STUCK_AFTER_MS) {
    return "stuck";
  }
  if (elapsedMs >= VISIBLE_AFTER_MS) {
    return "waiting";
  }
  return "quiet";
};

const containerStyle: CSSProperties = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
  height: "100dvh",
  justifyContent: "center",
  padding: "1.5rem",
  textAlign: "center",
};

const messageStyle: CSSProperties = {
  color: "var(--color-foreground)",
  fontSize: "0.875rem",
  margin: 0,
};

const hintStyle: CSSProperties = {
  color: "var(--color-muted-foreground)",
  fontSize: "0.8125rem",
  margin: 0,
  maxWidth: "28rem",
};

const buttonStyle: CSSProperties = {
  background: "var(--color-background)",
  border: "1px solid var(--color-input)",
  borderRadius: "0.375rem",
  color: "var(--color-foreground)",
  cursor: "pointer",
  fontSize: "0.875rem",
  padding: "0.375rem 0.875rem",
};

const reload = () => {
  window.location.reload();
};

export const AuthBootstrap = () => {
  const [phase, setPhase] = useState<BootstrapPhase>("quiet");

  useEffect(() => {
    const startedAt = Date.now();
    const sync = () => setPhase(phaseForElapsed(Date.now() - startedAt));
    const timers = [setTimeout(sync, VISIBLE_AFTER_MS), setTimeout(sync, STUCK_AFTER_MS)];

    // Background tabs throttle timers to roughly once a minute, so a tab that comes back to a
    // still-stuck bootstrap could sit on "waiting" long after the deadline passed. Re-derive from
    // the clock on the visibility edge — returning to a backgrounded tab is the exact case this
    // screen exists for (B-1927).
    document.addEventListener("visibilitychange", sync);

    return () => {
      for (const timer of timers) {
        clearTimeout(timer);
      }
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  if (phase === "quiet") {
    return null;
  }

  return (
    <div style={containerStyle}>
      {/* One live region across both phases, so a reader told "signing you in" also hears it fail. */}
      <p role="status" style={messageStyle}>
        {phase === "waiting" ? "Signing you in…" : "We couldn't finish signing you in."}
      </p>
      {phase === "stuck" && (
        <>
          <p style={hintStyle}>This can happen after a tab has sat in the background for a while.</p>
          <button onClick={reload} style={buttonStyle} type="button">
            Reload
          </button>
        </>
      )}
    </div>
  );
};
